/**
 * Documented-strategy library: wire types and display helpers.
 *
 * These are TypeScript mirrors of the Rust wire types in
 * `program/src/strategy_library.rs` (LibraryEntry, Requirements, Rebalance,
 * ExpectedRange, CostProfile, BacktestWindow, OperatingProcedure,
 * LibrarySourceClaim/LibraryCitationClaim/LibraryNumericClaim, Evidence,
 * Eligibility, Reason) plus the T4c overlay fields `program/src/api/library_api.rs`'s
 * `entry_view_json` adds to every entry (evidence_declared, evidence_basis,
 * blockers, verification_kind, admission, and the optional replication/
 * regime_disclosure) and the `GET /api/library` response wrapper itself.
 * Field names and enum spellings are the server's; do not rename them here.
 * Optionality mirrors serde exactly: a field with `#[serde(default)]` alone
 * (no `skip_serializing_if`) is ALWAYS present on the wire and typed
 * `T | null`, never `T | undefined`; a field with `skip_serializing_if` is
 * genuinely omittable and typed `T | undefined` (written as `field?: T`)
 * with no `null` in its type, since the key is absent, not nulled. Ratios
 * are fractions on the wire, as everywhere else (0.15 = 15%).
 */

import { evidenceChip } from './libraryCopy';

/**
 * How far the platform has verified an entry (Rust `Evidence`). `'agent_verified'`
 * (L1, `product-mandate/AUTONOMOUS_LIBRARY_CURATION_PLAN.md` section 2.2): a genuinely distinct fifth label, never
 * interchangeable with `'replicated_on_platform'` -- see `verification_kind` on `LibraryEntry` for who (human vs a
 * certified agent reviewer) produced whichever of the two labels is present.
 */
export type Evidence =
  | 'replicated_on_platform'
  | 'agent_verified'
  | 'reference_only'
  | 'unverified'
  | 'platform_capability_blocked';

/** Alias kept so existing call sites read the same. */
export type EvidenceLabel = Evidence;

/**
 * T4c (`T4_FIXED_RULE_ENDPOINT_PLAN.md` section 2.1 "Library and proposal overlay (T4c)"): `"declared"` when the
 * label is exactly the seed's own claim (no run considered, or none promotable); `"derived"` when the label came
 * from `derive_evidence` re-verifying a stored run.
 */
export type EvidenceBasis = 'declared' | 'derived';

/**
 * The tenant-safe (gross-only, no net figures) view of a promoted run, present only when a genuine run+review
 * promoted the label (Rust `replication::evidence::ReplicationSummary`, redacted -- see `api/library_api.rs`'s
 * `entry_view_json`). Omitted entirely (never `null`) when there is nothing to show -- the key itself is absent
 * from the JSON, not set to `null`.
 */
export interface ReplicationOverlay {
  run_id: string;
  reviewed_by: string;
  reviewed_at: string;
  fixture_id: string;
  window_start: string;
  window_end: string;
  sharpe_gross: number;
  cagr_gross: number;
  max_drawdown_gross: number;
  series_digest_gross: string;
  series_digest_net: string;
  same_person_requested_and_reviewed: boolean;
}

/**
 * `{"status": "admitted"}`, `{"status": "paper_only", "reasons": [...]}` or `{"status": "held", "reasons": [...]}`
 * (Rust `replication::admission::Admission`). `paper_only` (quant council R40, 2026-10-16): gate 9.10 was
 * inconclusive (bootstrap-p5 net Sharpe below the floor, point estimate positive) -- proposable for a paper plan
 * only, never promoted to live while this is the current verdict.
 */
export type Admission = { status: 'admitted' } | { status: 'paper_only'; reasons: string[] } | { status: 'held'; reasons: string[] };

/** One R40 evaluation of an entry/version (Rust `replication_robustness::store::EvaluationRecord`). */
export interface AdmissionEvaluationRecord {
  evaluated_at: string;
  run_id: string;
  rule_hash: string;
  config_version: string;
  window_start: string;
  window_end: string;
  n_bars: number;
  bootstrap_net_sharpe_p5: number;
  bootstrap_net_sharpe_p50: number;
}

/**
 * R40 disclosure counters: how many times this entry/version has been evaluated under R40 (one per new certified
 * fixture, never on demand) and the certified window of each. `evaluations: 0` is the honest answer for an entry
 * whose pipeline has not written a trace yet. Optional only so pre-R40 fixtures still type-check.
 */
export interface AdmissionTrace {
  evaluations: number;
  records: AdmissionEvaluationRecord[];
}

/**
 * W5.6 (gap-closure plan, section 4): the regime mix the entry's robustness check covered, surfaced ALONGSIDE
 * `admission`, never folded into it (Rust `replication::admission::RegimeDisclosure`). Omitted entirely (never
 * `null`) when there is nothing to disclose yet.
 */
export interface RegimeDisclosure {
  low_pct: number;
  medium_pct: number;
  high_pct: number;
  /** Total bars (summed across folds) the split above was computed over. */
  n_bars_covered: number;
}

export type RebalanceKind = 'daily' | 'monthly';

export interface Rebalance {
  kind: RebalanceKind;
  /** Exact timing and calendar caveats, in words. */
  detail: string;
}

/** Whether the published rule rebalances jointly across instruments. */
export type CrossAssetRebalance = 'needed' | 'preferred' | 'not_needed';

/** Measured gross exposure of a reference implementation (Rust `GrossExposureStats`). */
export interface GrossExposureStats {
  n_month_ends: number;
  first_month_end: string;
  last_month_end: string;
  mean: number;
  median: number;
  p90: number;
  max: number;
  min: number;
  source: string;
  note: string;
}

export interface LibraryRequirements {
  needs_shorting: boolean;
  /** Gross notional as a multiple of sleeve equity (1.0 = fully invested, unleveraged). */
  min_leverage_gross: number;
  /**
   * Rust `#[serde(default)]` with no `skip_serializing_if`: the key is ALWAYS present on the wire (never omitted),
   * `null` when not measured -- not an optional/omittable field.
   */
  gross_exposure_reference: GrossExposureStats | null;
  needs_portfolio_vol_target: boolean;
  /**
   * Largest absolute weight per instrument as a multiple of sleeve equity. Same wire behaviour as
   * `gross_exposure_reference`: always present, `null` = not bounded.
   */
  max_abs_weight_per_instrument: number | null;
  cross_asset_rebalance: CrossAssetRebalance;
  /** Mandate vocabulary (e.g. `us_etf`, `fx`, `crypto_spot`). ALL must be permitted. */
  asset_classes: string[];
  /** Venues the entry can trade on at all (paper or live). */
  venues_supported: string[];
  /** History needed to compute the first signal (not to judge the strategy). */
  min_history_years: number;
  notes: string[];
}

/** `performance_band`: results are expected inside it. `integrity_bound`: outside it, suspect the test. */
export type RangeRole = 'performance_band' | 'integrity_bound';

/**
 * A range with the place the number came from. A `null` bound means no bound
 * is known on that side (never a guess).
 */
export interface ExpectedRange {
  metric: string;
  low: number | null;
  high: number | null;
  role: RangeRole;
  source: string;
  note: string;
}

export interface CostProfile {
  /** Annual turnover as a multiple of sleeve equity; null when not measured. */
  turnover_per_year_estimate: number | null;
  /** Signal flips per year in the reference run: a trade-count proxy, NOT turnover. */
  signal_flips_per_year_shadow: number | null;
  note: string;
}

/**
 * The independent-replication backtest's own window, as structured data (Rust `BacktestWindow`).
 */
export interface BacktestWindow {
  /** `YYYY-MM-DD`. */
  start: string;
  /** `YYYY-MM-DD`. */
  end: string;
  /**
   * The largest peak-to-trough decline of the underlying BUY-AND-HOLD benchmark (not the strategy's own drawdown)
   * actually observed in this window, as a positive fraction (0.15 = 15%); `null` when genuinely not measured.
   */
  max_observed_drawdown: number | null;
}

/**
 * How a decision made by the rule becomes an action, as data rather than prose (Rust `OperatingProcedure`).
 * Present only on a version-2-or-later entry; the key is omitted entirely (never `null`) when absent, matching
 * the Rust field's `#[serde(default, skip_serializing_if = "Option::is_none")]`.
 */
export interface OperatingProcedure {
  /** `"monthly"` or `"daily"` -- mirrors `Rebalance.kind` in the compiled rule's own vocabulary. */
  decision_schedule: string;
  /** How often the standing decision is checked/replayed even when it does not change. */
  evaluation: string;
  /** When a decision made at a bar's close may first be acted on. */
  actionable_after: string;
  /** `"on_decision"` or `"every_bar"`. */
  rebalance_policy: string;
  /** The pre-registered live delay, in bars, between a decision and the bar it is acted on. */
  expected_delay_bars: number;
  /** `"next_open"` or `"assisted"`. */
  execution_assumption: string;
  /** How a missed decision is made up; the only value either compiled rule uses today is `"cap"`. */
  catch_up: string;
}

/** One human-stated numeric fact a cited source is claimed to say (Rust `LibraryNumericClaim`). */
export interface LibraryNumericClaim {
  /** Free-text metric name, e.g. "Sharpe Ratio improvement over buy-and-hold". */
  metric: string;
  value: number;
  /** Optional period the number applies to, e.g. "1973-2008"; `null` when not stated. Always present, never omitted. */
  period: string | null;
}

/** What a `LibrarySourceClaim` claims its cited source says (Rust `LibraryCitationClaim`). */
export interface LibraryCitationClaim {
  quotes: string[];
  numbers: LibraryNumericClaim[];
  /** Always present, never omitted; `null` when not stated. */
  sample_period: string | null;
}

/**
 * A human-authored, checkable claim about one of this entry's own `sources` (Rust `LibrarySourceClaim`).
 * Deliberately NOT LLM-extracted.
 */
export interface LibrarySourceClaim {
  /** Index into this entry's own `sources` -- which cited source this claim is about. */
  source_index: number;
  claim: LibraryCitationClaim;
}

export interface LibraryEntry {
  id: string;
  /** Bumped on any change to the rule, parameters or ranges. */
  version: number;
  name: string;
  /** Source citations as plain strings. */
  sources: string[];
  /**
   * Human-authored, structured, checkable claims about what specific `sources` (by index) say. Omitted entirely
   * (never `[]` sent over the wire) when there are none, matching the Rust field's
   * `#[serde(default, skip_serializing_if = "Vec::is_empty")]`.
   */
  citation_claims?: LibrarySourceClaim[];
  /** The exact, unambiguous rule. */
  rule_text: string;
  /** Where the reference implementation lives and its pin. */
  reference_implementation: string;
  /** Instruments in the mandate's naming (e.g. `SPY`, `BTC/USD`). ALL are required by the rule. */
  instruments: string[];
  requirements: LibraryRequirements;
  rebalance: Rebalance;
  /**
   * COUNCIL Ruling 10: present only on a version-2-or-later entry; omitted entirely (never `null`) when absent,
   * matching the Rust field's `#[serde(default, skip_serializing_if = "Option::is_none")]`.
   */
  operating_procedure?: OperatingProcedure;
  /** Fixed at the published values; the agent chooses among entries, it never tunes one. */
  parameters: Record<string, unknown>;
  /** The first entry is the primary one. */
  expected_ranges: ExpectedRange[];
  /** WP9.8: the window the independent-replication backtest actually ran over, as structured data a check can read. */
  backtest_window: BacktestWindow;
  /**
   * WP9.9: every deviation between what the platform will actually run and what the rule as published specifies.
   * Explicitly `[]` (never omitted) when there is truly nothing to disclose.
   */
  known_deviations: string[];
  failure_regimes: string[];
  cost_profile: CostProfile;
  /** The EFFECTIVE (derived) label as of T4c -- see `evidence_declared` for the raw seed value. */
  evidence: Evidence;
  /** `YYYY-MM-DD`: when the rule text was last checked against the reference. */
  last_verified: string;
  /** What exactly was and was not verified on that date. */
  verification_note: string;
  retirement_rule: string;
  /**
   * Whether this entry is shown to the free, paper-only "Observer" tier. Omitted entirely (never `false` sent over
   * the wire) for the common, non-flagship case, matching the Rust field's
   * `#[serde(default, skip_serializing_if = "is_false")]`.
   */
  flagship?: boolean;

  // -------------------------------------------------------------------------
  // T4c overlay (`T4_FIXED_RULE_ENDPOINT_PLAN.md` section 2.1): NOT part of the Rust `LibraryEntry` struct itself --
  // `api/library_api.rs`'s `entry_view_json` adds these to EVERY entry's JSON on EVERY `GET /api/library` call,
  // whether or not eligibility was requested. `evidence_declared`/`evidence_basis`/`blockers`/`verification_kind`/
  // `admission` are therefore always present on the wire (required here, not optional); `replication` and
  // `regime_disclosure` are each omitted entirely (never `null`) when there is nothing to show for this entry.
  // -------------------------------------------------------------------------

  /** The raw, declared value `evidence` may have been overwritten from (always present). */
  evidence_declared: Evidence;
  /** Always present. */
  evidence_basis: EvidenceBasis;
  /** Present only when a genuine run+review promoted this entry's label. Omitted, never `null`, when absent. */
  replication?: ReplicationOverlay;
  /** Stable codes explaining why `evidence` is not (or no longer) `replicated_on_platform`/`agent_verified`. Always present, may be `[]`. */
  blockers: string[];
  /** Independent of `evidence` -- may this entry be proposed at all. Always present. */
  admission: Admission;
  admission_trace?: AdmissionTrace;
  /**
   * L3 (`AUTONOMOUS_LIBRARY_CURATION_PLAN.md` section 2.2), additive alongside `evidence_basis`: `"human"` when
   * `evidence` is `'replicated_on_platform'`, `"agent"` when it is `'agent_verified'`, `null` otherwise. Always
   * present (never omitted).
   */
  verification_kind: 'human' | 'agent' | null;
  /**
   * W5.6: the regime mix this entry's robustness check covered, surfaced alongside `admission` (never folded into
   * it). Omitted entirely (never `null`) when there is nothing to disclose yet.
   */
  regime_disclosure?: RegimeDisclosure;
}

/** Who is responsible for a reason (Rust `Origin`). One origin per reason code. */
export type ReasonOrigin = 'mandate' | 'platform' | 'advisory';

/** What a reason blocks (Rust `Blocks`). */
export type ReasonBlocks = 'paper_and_live' | 'live_only' | 'nothing';

/** Machine codes, in the order Rust declares them (reasons arrive sorted by this order). */
export type ReasonCode =
  // origin: mandate
  | 'shorting_not_permitted'
  | 'leverage_exceeds_cap'
  | 'gross_exceeds_exposure_limit'
  | 'asset_class_not_permitted'
  | 'instruments_not_allowed'
  | 'instrument_denied'
  | 'venue_not_permitted'
  // origin: platform
  | 'shorting_unsupported'
  | 'leverage_unsupported'
  | 'portfolio_vol_target_unsupported'
  | 'weight_above_one_unsupported'
  | 'cross_asset_rebalance_unsupported'
  | 'not_live_ready'
  // origin: advisory
  | 'cross_asset_rebalance_approximated'
  | 'peak_gross_may_clip';

export interface Reason {
  code: ReasonCode;
  origin: ReasonOrigin;
  blocks: ReasonBlocks;
  /** Plain language, safe to show to the user. */
  message: string;
}

/** Deterministic result of checking an entry against a mandate and the platform (Rust `Eligibility`). */
export interface Eligibility {
  /** May be proposed and paper-traded. */
  eligible_paper: boolean;
  /** May additionally go live (paper-eligible and nothing blocks live). */
  eligible_live: boolean;
  /** EVERY reason, blocking and advisory, ordered by code. */
  reasons: Reason[];
}

export type LibraryEntryView = LibraryEntry & {
  /** Present only when the request asked for eligibility and an active mandate exists. */
  eligibility?: Eligibility;
};

export interface LibraryResponse {
  entries: LibraryEntryView[];
  /** Set when eligibility was requested (`?for=active-mandate`); null when there is no active mandate. */
  mandate_version?: number | null;
  notice?: string;
}

// ---------------------------------------------------------------------------
// Evidence wording
// ---------------------------------------------------------------------------

/**
 * The exact wording tests and screens rely on. Lower case on purpose: it reads
 * as a label inside a badge and the storyboard matches this text.
 */
export function evidenceLabelText(label: EvidenceLabel): string {
  return evidenceChip(label).label;
}

/** One-line explanation shown beside the label so nobody has to guess what it means. */
export function evidenceLabelHelp(label: EvidenceLabel): string {
  return evidenceChip(label).help;
}

// ---------------------------------------------------------------------------
// Expected ranges
// ---------------------------------------------------------------------------

const METRIC_LABELS: Record<string, string> = {
  sharpe_net: 'Net Sharpe (after costs)',
  sharpe_gross: 'Gross Sharpe (before costs)',
  sharpe: 'Sharpe',
  max_drawdown: 'Worst fall (max drawdown)',
  realised_annual_volatility: 'Realised annual volatility',
};

/** A readable name for a metric id; unknown ids are spelled out, never hidden. */
export function rangeMetricLabel(metric: string): string {
  return METRIC_LABELS[metric] ?? metric.replace(/_/g, ' ');
}

/** Drawdowns, volatilities and returns are fractions on the wire (0.25 = 25%). */
function isFractionMetric(metric: string): boolean {
  return /drawdown|volatility|return|cagr/.test(metric);
}

function formatBound(metric: string, value: number): string {
  if (isFractionMetric(metric)) return `${Math.round(value * 1000) / 10}%`;
  return String(Math.round(value * 1000) / 1000);
}

/** The bounds of one range in words: "0.2 to 1", "at least 0.2", "at most 25%", "no known bound". */
export function rangeBoundsText(r: ExpectedRange): string {
  const { low, high, metric } = r;
  if (low !== null && high !== null) return `${formatBound(metric, low)} to ${formatBound(metric, high)}`;
  if (low !== null) return `at least ${formatBound(metric, low)}`;
  if (high !== null) return `at most ${formatBound(metric, high)}`;
  return 'no known bound';
}

/** ONE range as "<metric label>: <bounds>". Never invents a number for a missing bound. */
export function expectedRangeText(r: ExpectedRange): string {
  return `${rangeMetricLabel(r.metric)}: ${rangeBoundsText(r)}`;
}

/** What a range's role means for the reader. */
export function rangeRoleText(role: RangeRole): string {
  return role === 'integrity_bound'
    ? 'integrity bound (a result outside it means suspect the test; inside it says nothing about quality)'
    : 'performance band (where results are expected to fall)';
}

/** The entry's primary range (the first one), or null when it publishes none. */
export function primaryRange(entry: Pick<LibraryEntry, 'expected_ranges'>): ExpectedRange | null {
  return entry.expected_ranges[0] ?? null;
}

// ---------------------------------------------------------------------------
// Eligibility reasons
// ---------------------------------------------------------------------------

/** Every reason that blocks something (paper and live, or live only). */
export function blockingReasons(elig: Eligibility): Reason[] {
  return elig.reasons.filter((r) => r.blocks !== 'nothing');
}

/** Reasons that stop the entry being proposed or paper-traded at all. */
export function paperBlockingReasons(elig: Eligibility): Reason[] {
  return elig.reasons.filter((r) => r.blocks === 'paper_and_live');
}

/** Reasons that only stop it going live (paper is fine). */
export function liveOnlyReasons(elig: Eligibility): Reason[] {
  return elig.reasons.filter((r) => r.blocks === 'live_only');
}

/** Non-blocking warnings about how it will run. */
export function advisoryReasons(elig: Eligibility): Reason[] {
  return elig.reasons.filter((r) => r.origin === 'advisory');
}

/** Reasons split by who is responsible, keeping the server's order inside each group. */
export function groupReasonsByOrigin(reasons: Reason[]): Record<ReasonOrigin, Reason[]> {
  return {
    mandate: reasons.filter((r) => r.origin === 'mandate'),
    platform: reasons.filter((r) => r.origin === 'platform'),
    advisory: reasons.filter((r) => r.origin === 'advisory'),
  };
}
